import { useToast } from '@/hooks/use-toast'
import apiClient from '@/lib/apiClient'
import { cn } from '@/lib/utils'
import { Testimonial, TestimonialFormData } from '@/types/api'
import { Loader2, Star, UploadCloud, X } from 'lucide-react'
import React, { useState } from 'react'

interface TestimonialFormProps {
  testimonial?: Testimonial
  onSuccess: () => void
  onCancel: () => void
}

export function TestimonialForm({ testimonial, onSuccess, onCancel }: TestimonialFormProps) {
  const { toast } = useToast()
  const [loading, setLoading] = useState(false)
  const [formData, setFormData] = useState<TestimonialFormData>({
    name: testimonial?.name || '',
    title: testimonial?.title || '',
    content: testimonial?.content || '',
    avatar_url: testimonial?.avatar_url || '',
    rating: testimonial?.rating || 5,
    is_active: testimonial?.is_active ?? true,
    sort_order: testimonial?.sort_order || 0,
    created_at: testimonial?.created_at ? testimonial.created_at.split('T')[0] : '',
  })
  const [previewUrl, setPreviewUrl] = useState<string | null>(testimonial?.avatar_url || null)

  const isEdit = !!testimonial

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0]
      setFormData((prev) => ({ ...prev, avatar: file }))
      const url = URL.createObjectURL(file)
      setPreviewUrl(url)
    }
  }

  const removeImage = () => {
    setFormData((prev) => ({ ...prev, avatar: undefined, avatar_url: '' }))
    setPreviewUrl(null)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)

    try {
      if (isEdit && testimonial) {
        await apiClient.updateTestimonial(testimonial.id, formData)
        toast({
          title: 'Success',
          description: 'Testimonial updated successfully',
        })
      } else {
        await apiClient.createTestimonial(formData)
        toast({
          title: 'Success',
          description: 'Testimonial created successfully',
        })
      }
      onSuccess()
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to save testimonial',
        variant: 'destructive',
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            placeholder="Reviewer Name"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Title/Position</label>
          <input
            type="text"
            value={formData.title}
            onChange={(e) => setFormData((prev) => ({ ...prev, title: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            placeholder="e.g. CEO of Company X"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Avatar Image</label>
          <div className="flex items-start gap-4">
            {previewUrl ? (
              <div className="relative w-24 h-24 rounded-lg overflow-hidden border border-gray-200 group">
                <img src={previewUrl} alt="Preview" className="w-full h-full object-cover" />
                <button
                  type="button"
                  onClick={removeImage}
                  className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ) : (
              <label className="flex flex-col items-center justify-center w-24 h-24 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-primary-500 hover:bg-primary-50 transition-colors">
                <UploadCloud className="w-6 h-6 text-gray-400" />
                <span className="text-xs text-gray-500 mt-1">Upload</span>
                <input
                  type="file"
                  className="hidden"
                  accept="image/*"
                  onChange={handleFileChange}
                />
              </label>
            )}
            <div className="flex-1">
              <p className="text-sm text-gray-500 mb-2">Upload an avatar image (JPG, PNG, WEBP).</p>
            </div>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Sort Order</label>
          <input
            type="number"
            value={formData.sort_order}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, sort_order: parseInt(e.target.value) || 0 }))
            }
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            placeholder="0"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Rating</label>
        <div className="flex items-center gap-2">
          {[1, 2, 3, 4, 5].map((star) => (
            <button
              key={star}
              type="button"
              onClick={(e) => {
                e.preventDefault()
                setFormData((prev) => ({ ...prev, rating: star }))
              }}
              className={cn(
                'w-8 h-8 rounded-full transition-all flex items-center justify-center',
                formData.rating >= star
                  ? 'text-yellow-400 bg-yellow-50 hover:bg-yellow-100'
                  : 'text-gray-300 hover:bg-gray-100'
              )}
            >
              <Star
                className={cn(
                  'w-5 h-5 pointer-events-none',
                  formData.rating >= star && 'fill-current'
                )}
              />
            </button>
          ))}
          <span className="ml-2 font-medium text-gray-700">{formData.rating}/5</span>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Content</label>
        <textarea
          value={formData.content}
          onChange={(e) => setFormData((prev) => ({ ...prev, content: e.target.value }))}
          rows={5}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          placeholder="Testimonial content..."
          required
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            checked={formData.is_active}
            onChange={(e) => setFormData((prev) => ({ ...prev, is_active: e.target.checked }))}
          />
          <span className="text-sm text-gray-700">Active</span>
        </label>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Created At (Date)</label>
          <input
            type="date"
            value={formData.created_at}
            onChange={(e) => setFormData((prev) => ({ ...prev, created_at: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>
      </div>

      <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
        <button
          type="button"
          onClick={onCancel}
          className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={loading}
          className="px-6 py-2 bg-primary-600 text-white rounded-lg font-semibold hover:bg-primary-700 transition-colors disabled:opacity-50 flex items-center gap-2"
        >
          {loading && <Loader2 className="w-4 h-4 animate-spin" />}
          {isEdit ? 'Update Testimonial' : 'Create Testimonial'}
        </button>
      </div>
    </form>
  )
}
